/*
 * Copyright (c) 2018-present OOO “INTEGRAL”, gaz-connect.ru. All Rights Reserved.
 * See LICENSE.txt for license information.
 **/
import { Component, OnInit, Input, OnDestroy, ViewChildren, QueryList } from '@angular/core';
import { Router } from '@angular/router';
import { ModalController, AlertController, IonCheckbox } from '@ionic/angular';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { takeUntil } from 'rxjs/operators';

import { ProductsProvider } from 'src/app/providers/products/products';
import { UtilsService } from 'src/app/services/utils/utils.service';
import { CarsProvider } from 'src/app/providers/cars/cars';
import { AppMetricaService } from 'src/app/services/app-metrica/app-metrica.service';
import { PdfViewerPage } from 'src/app/pages/pdf-viewer/pdf-viewer.page';
import { IUserState } from 'src/app/store/state';
import { selectUserData } from 'src/app/store/selectors';
import { PhoneFormatPipe } from 'src/app/pipes/phone-format/phone-format.pipe';
import { INTERCEPTOR_ERRORS } from 'src/app/pages/constants';
import { StsEntryComponent } from 'src/app/components/sts-entry/sts-entry.component';
import { PROVIDER_PRODUCTS_IDS } from 'src/app/pages/constants';

const preConnectedStatusses = ['need_add', 'adding', 'pending', 'error'];
const preDisconnectedStatusses = ['need_remove', 'removing', 'pending_remove'];

@Component({
  selector: 'app-product-subscription',
  templateUrl: './product-subscription.page.html',
  styleUrls: ['./product-subscription.page.scss'],
})
export class ProductSubscriptionPage implements OnInit, OnDestroy {
  @Input() product: any;
  @ViewChildren('itemCheckbox') itemCheckboxes: QueryList<IonCheckbox>;

  prevLink: string;
  isLoading = true;
  allItemsSelected = false;
  subsForm: FormGroup;
  searchValue = '';
  isRequestLoading = false;
  userData: any;
  unsubscribe$: Subject<void>;

  currentFilter = 'notConnected';
  carsList: any[] = [];
  allSubscriptions: any[] = [];
  currentCarList: any[] = [];
  isHelpModalOpen = false;

  constructor(
    private modalCtrl: ModalController,
    private cars: CarsProvider,
    private products: ProductsProvider,
    private utils: UtilsService,
    private formBuilder: FormBuilder,
    private router: Router,
    private alertCtrl: AlertController,
    private appMetrica: AppMetricaService,
    private store: Store<{ user: IUserState }>,
    private phoneFormatPipe: PhoneFormatPipe,
  ) {
    this.unsubscribe$ = new Subject<void>();
  }

  fetchCars() {
    return this.cars
      .fetchList()
      .then(([cars]: any[]) => {
        this.carsList = cars;
      })
      .catch((err: Error) => {
        if (err.message === INTERCEPTOR_ERRORS.NO_INTERNET_CONNECTION) {
          return;
        }
        const message = this.utils.tr('IDT_CARS_LIST_LOADING_ERROR');
        this.utils.tr(message);
      });
  }

  fetchSubscriptions() {
    return this.products
      .fetchMySubscriptionsWithCarsConnected()
      .then((data: any) => {
        this.allSubscriptions = data.filter(
          (subs) => subs.serviceName === this.product.providerProductId,
        );
      })
      .catch((err: Error) => {
        if (err.message === INTERCEPTOR_ERRORS.NO_INTERNET_CONNECTION) {
          return;
        }
        const message = this.utils.tr('IDT_CARS_LIST_LOADING_ERROR');
        this.utils.presentToast({ message });
      });
  }

  getCarsWithSubs() {
    return this.carsList
      .filter(
        (car) =>
          car.subscription
          && [...preDisconnectedStatusses, 'active'].indexOf(car.subscription.status) > -1,
      )
      .sort((a) => (a.subscription && a.subscription.status === 'active' ? -1 : 1));
  }

  getCarsWithoutSubs() {
    return this.carsList
      .filter(
        (car) =>
          !car.subscription
          || (car.subscription && preConnectedStatusses.indexOf(car.subscription.status) > -1),
      )
      .sort((a) => (a.subscription ? 1 : -1));
  }

  carProductSubscriptionUpdateGuard(car): boolean {
    let permission = 'services.update';
    /** для этих услуг отдельное полномочие https://redmine.gaz-connect.ru/issues/14403#note-6 */
    const servicesWithItsOwnPermission = ['RNISMO', 'GAZFleet', 'ELECTRONIC_WAYBILL'];
    if (servicesWithItsOwnPermission.includes(this.product.providerProductId)) {
      permission = `services.${this.product.providerProductId}.update`;
    }
    return car.role.permissions.includes(permission);
  }

  getCurrentCarList() {
    return this.currentFilter === 'notConnected'
      ? this.getCarsWithoutSubs()
      : this.getCarsWithSubs();
  }

  getNumberOfCurrentEnabledCars() {
    if (this.currentFilter === 'notConnected') {
      return this.applySearch(this.getCurrentCarList()).filter((car) => !car.subscription).length;
    }
    return this.applySearch(this.getCurrentCarList()).filter(
      (car) => car.subscription && car.subscription.status === 'active',
    ).length;
  }

  isCarDisabled(car) {
    if (!car.subscription) {
      return false;
    }
    const statusses
      = this.currentFilter === 'notConnected' ? preConnectedStatusses : preDisconnectedStatusses;

    return statusses.indexOf(car.subscription.status) > -1;
  }

  createGroup(): void {
    const selectedCarsUuids = this.getSelectedCarsUuids();
    const group = this.formBuilder.group({});
    this.currentCarList.forEach((car) => {
      group.addControl(
        car.uuid,
        this.formBuilder.control({
          value: selectedCarsUuids.indexOf(car.uuid) > -1,
          disabled: this.isCarDisabled(car),
        }),
      );
    });

    this.subsForm = group;
  }

  toggleList() {
    setTimeout(() => {
      this.allItemsSelected = !this.allItemsSelected;
      this.itemCheckboxes.forEach((cbx) => {
        // не отмечаем те, на которые нет прав
        if (
          this.carProductSubscriptionUpdateGuard(this.carsList.find((c) => c.uuid === cbx.name))
        ) {
          cbx.checked = this.allItemsSelected;
        }
      });
    });
  }

  getSubscriptionStatus(car) {
    if (!car.subscription) {
      return null;
    }
    if (preConnectedStatusses.indexOf(car.subscription.status) > -1) {
      return this.utils.tr('IDT_SUBS_STATUS_SUBSCRIBING');
    }
    if (preDisconnectedStatusses.indexOf(car.subscription.status) > -1) {
      return this.utils.tr('IDT_SUBS_STATUS_UNSUBSCRIBING');
    }
    return null;
  }

  applySearch(carsList) {
    const filteredCarUuids = this.carsList.map((car) => car.uuid);
    return carsList
      .filter((car) => {
        return (
          (car.parameters.name
            && car.parameters.name.toLowerCase().includes(this.searchValue.toLowerCase()))
          || (car.parameters.stateNumber
            && car.parameters.stateNumber.toLowerCase().includes(this.searchValue.toLowerCase()))
        );
      })
      .filter((car) => filteredCarUuids.indexOf(car.uuid) > -1);
  }

  doSearch(event) {
    this.searchValue = event.target.value;
    if (this.searchValue.length) {
      this.currentCarList = this.applySearch(this.getCurrentCarList());
    } else {
      this.currentCarList = this.getCurrentCarList();
    }
    this.createGroup();

    // сбрасываем состояние чекбокса "Выбрать все" после поиска,
    // если есть хотя бы одно невыбранное авто
    setTimeout(() => {
      let allChecked = true;
      this.itemCheckboxes.forEach((cbx) => {
        if (!cbx.checked && !cbx.disabled) {
          allChecked = false;
        }
      });

      this.allItemsSelected = allChecked;
    });
  }

  isRNIS() {
    return this.product.providerProductId === PROVIDER_PRODUCTS_IDS.RNISMO;
  }

  isPenaltiesGIBDD() {
    return this.product.providerProductId === PROVIDER_PRODUCTS_IDS.PENALTIES_GIBDD;
  }

  checkUserParams() {
    const emptyParams = this.product.features.userParameters.parameters.filter(
      (param) => param.required && !param.value,
    );
    emptyParams.forEach((param) => {
      const message = `${this.utils.tr('IDT_NO_PARAM_VALUE')}: ${param.propertyLabel}`;
      this.utils.presentToast({ message });
    });

    const invalidParams = this.product.features.userParameters.parameters.filter((param) => {
      if (!param.value) {
        return false;
      }
      const regexp = new RegExp(param.pattern);
      return !regexp.test(param.value);
    });
    invalidParams.forEach((param) => {
      const message = `${this.utils.tr('IDT_INVALID_PARAM_VALUE')}: ${param.propertyLabel}`;
      this.utils.presentToast({ message });
    });

    // eslint-disable-next-line  @typescript-eslint/restrict-plus-operands
    return emptyParams.length + invalidParams.length;
  }

  getUserParams() {
    const userParams = {};
    this.product.features.userParameters.parameters
      .filter((param) => param.value)
      .forEach((param) => {
        userParams[param.propertyName] = param.value;
      });
    return userParams;
  }

  getProductPriceId() {
    return this.product.prices && this.product.prices.length ? this.product.prices[0].id : 1;
  }

  async toggleSubscription() {
    const carsUuids = this.getSelectedCarsUuids();

    if (carsUuids.length > 0) {
      if (this.currentFilter === 'notConnected') {
        if (this.checkUserParams() === 0) {
          let promise;
          this.isRequestLoading = true;
          if (this.isPenaltiesGIBDD()) {
            // получаем список выбранных автомобилей
            const [cars] = await this.cars.fetchList({
              filter: {
                uuid: carsUuids,
              },
            });

            // получаем список автомобилей без СТС
            const carsWithoutSTS = cars?.filter((car) => !car?.parameters?.sts);
            let modal = null;
            if (carsWithoutSTS && carsWithoutSTS.length) {
              modal = await this.modalCtrl.create({
                component: StsEntryComponent,
                componentProps: {
                  carsListWithoutSTS: carsWithoutSTS,
                },
              });
              await modal.present();
            }

            try {
              if (modal) {
                const { data } = await modal.onWillDismiss();
                if (data.message) {
                  throw new Error(data.message);
                }
              }
              promise = Promise.all(
                carsUuids.map((carUuid) => {
                  const params = {
                    carUuid,
                    priceId: this.getProductPriceId(),
                    userParameters: this.getUserParams(),
                  };
                  return this.products.createProductSubscription(params);
                }),
              );
            } catch (err) {
              const message = this.utils.tr('IDT_ADDING_SERVICE_ERROR');
              this.utils.presentToast({ message });
              this.isRequestLoading = false;
              return;
            }
          } else if (this.isRNIS()) {
            promise = Promise.all(
              carsUuids.map((carUuid) => {
                const params = {
                  carUuid,
                  priceId: this.getProductPriceId(),
                  userParameters: this.getUserParams(),
                };
                return this.products.createProductSubscription(params);
              }),
            );
          } else {
            const params = {
              carsUuids,
              priceId: this.getProductPriceId(),
              userParameters: this.getUserParams(),
            };
            promise = this.products.addProductSubscription(this.product.id, params);
          }

          promise
            .then(() => {
              this.showAlertRequestSent();
            })
            .catch((err: Error) => {
              if (err.message === INTERCEPTOR_ERRORS.NO_INTERNET_CONNECTION) {
                return;
              }
              const message = this.utils.tr('IDT_ADDING_SERVICE_ERROR');
              this.utils.presentToast({ message });
            })
            .finally(() => {
              this.isRequestLoading = false;
            });
        }
      } else {
        this.showUnsubscriptionConfirmation(carsUuids);
      }
    } else {
      const message = this.utils.tr('IDT_NONE_CARS_WERE_SELECTED');
      this.utils.presentToast({ message });
    }
  }

  sendUnsubscribeRequest(carsUuids: any[]) {
    Promise.all(
      this.getCarsWithSubs()
        .filter((car) => carsUuids.indexOf(car.uuid) > -1)
        .map((car) => this.products.removeProductSubscription(car.subscription.id)),
    )
      .then(() => {
        this.showAlertRequestSent();
      })
      .catch((err: Error) => {
        if (err.message === INTERCEPTOR_ERRORS.NO_INTERNET_CONNECTION) {
          return;
        }
        const message = this.utils.tr('IDT_REMOVING_SERVICE_ERROR');
        this.utils.presentToast({ message });
      });
  }

  formatPhone(phone) {
    return this.phoneFormatPipe.transform(phone);
  }

  getUnsubscriptionConfirmationMessage(selectedCars: any[]) {
    let message = '';
    switch (this.product.providerProductId) {
      case 'GAZFleet':
        message = `
          <span class='ion-text-left'>
            ${this.utils.parseString(
              this.utils.getValueFromLanguageStrings('GAZFLEET_CLIENT_CANCELLING_NOTICE'),
              {
                SUPPORT_FEEDBACK_EMAIL:
                  this.utils.getValueFromLanguageStrings('SUPPORT_FEEDBACK_EMAIL'),
                GAZFLEET_CLIENT_CANCELLING_MAIL_BODY: encodeURIComponent(
                  this.utils.parseString(
                    this.utils.getValueFromLanguageStrings('GAZFLEET_CLIENT_CANCELLING_MAIL_BODY'),
                    { carVINs: selectedCars.map((car) => `\n${car?.vin}`) },
                  ),
                ),
                GAZFLEET_CLIENT_CANCELLING_MAIL_SUBJECT: encodeURIComponent(
                  this.utils.parseString(
                    this.utils.getValueFromLanguageStrings(
                      'GAZFLEET_CLIENT_CANCELLING_MAIL_SUBJECT',
                    ),
                    { userPhone: this.formatPhone(this.userData?.phone) },
                  ),
                ),
              },
            )}
          </span>
          <div class='ion-text-left'>
            <span>${this.utils.tr('IDT_ENTER_TEXT')}</span>
            <span class='text-primary'>${this.utils.tr('IDT_UNSUBSCRIBE')}</span>
            <span> ${this.utils.tr('IDT_TO_UNSUBSCRIBE_TEXT')}</span>
          </div>
        `;
        break;
      default:
        message = `
          <span class="text-grey">
            ${this.product.features.removeSubscriptionNotice}
          </span>
          <br><br>
          <span>${this.utils.tr('IDT_ENTER_TEXT')} </span>
          <span class='text-primary'>${this.utils.tr('IDT_UNSUBSCRIBE')}</span>
          <span> ${this.utils.tr('IDT_TO_UNSUBSCRIBE_TEXT')}</span>
        `;
        break;
    }
    return message;
  }

  async showUnsubscriptionConfirmation(carsUuids: any[]) {
    const selectedCars = this.carsList.filter(
      (car) => carsUuids.findIndex((carUuid) => car.uuid === carUuid) > -1,
    );

    const alert = await this.alertCtrl.create({
      mode: 'ios',
      header: this.utils.tr('IDT_ATTENTION'),
      cssClass: 'unsubscription-alert',
      message: this.getUnsubscriptionConfirmationMessage(selectedCars),
      inputs: [
        {
          placeholder: this.utils.tr('IDT_UNSUBSCRIBE'),
          type: 'text',
        },
      ],
      buttons: [
        {
          text: this.utils.tr('IDT_UNSUBSCRIBE'),
          handler: (data) => {
            const translatedWordDelete = this.utils.tr('IDT_UNSUBSCRIBE');

            if (data[0].toLowerCase() === translatedWordDelete.toLowerCase()) {
              this.sendUnsubscribeRequest(carsUuids);
              return true;
            }
            return false;
          },
        },
        {
          text: this.utils.tr('IDT_CANCELLATION'),
          role: 'cancel',
          handler: () => {
            this.reInitForm();
          },
        },
      ],
    });
    return alert.present();
  }

  async showAlertRequestSent() {
    const alert = await this.alertCtrl.create({
      mode: 'ios',
      subHeader: this.utils.tr('IDT_SUBSRIPTION_REQUEST_SENT_ALERT_TEXT'),
      buttons: [
        {
          text: this.utils.tr('IDT_CLOSE'),
          role: 'cancel',
          handler: () => {
            this.reInitForm();
          },
        },
      ],
    });

    await alert.present();
  }

  async openHelpModal(link: string) {
    const modal = await this.modalCtrl.create({
      component: PdfViewerPage,
      componentProps: {
        pdfSrc: link,
      },
    });

    this.isHelpModalOpen = true;

    modal.onDidDismiss().then(() => {
      setTimeout(() => {
        this.searchValue = '';
        this.isHelpModalOpen = false;
      });
    });
    return modal.present();
  }

  getSelectedCarsUuids() {
    if (!this.subsForm) {
      return [];
    }
    return Object.keys(this.subsForm.value).filter((key) => this.subsForm.value[key] === true);
  }

  isMaskEnabled(propertyName: string) {
    // по умолчанаю функция возвращает null, чтобы атрибут appIonMask удалился и поле ввода было без маски
    let mask = null;
    switch (propertyName) {
      case 'ipport':
        mask = ''; // маска динамическая, чтобы автоматически подсталять точки
        break;
      case 'stateNumber':
        mask = this.utils.getStateNumberMask();
        break;
      case 'phone':
        mask = this.utils.getPhoneMask();
        break;
      default:
        break;
    }
    return mask;
  }

  getMaskConfig(propertyName: string) {
    let config = null;
    switch (propertyName) {
      case 'ipport':
        config = this.utils.getIpPortMaskConfig();
        break;
      case 'stateNumber':
      case 'phone':
        config = {
          guide: true,
          showMask: false,
          keepCharPositions: true,
        };
        break;
      default:
        break;
    }
    return config;
  }

  segmentChanged(event) {
    this.currentFilter = event.detail.value;
    this.searchValue = '';
    this.allItemsSelected = false;
    this.currentCarList = this.getCurrentCarList();
    this.createGroup();
  }

  itemSelectedChange() {
    let allItemsChecked = true;
    this.itemCheckboxes.forEach((itemCheckbox) => {
      if (!itemCheckbox.checked && !itemCheckbox.disabled) {
        allItemsChecked = false;
      }
    });

    this.allItemsSelected = allItemsChecked;
  }

  reInitForm() {
    this.isLoading = true;
    this.searchValue = '';
    this.allItemsSelected = false;
    Promise.all([this.fetchCars(), this.fetchSubscriptions()])
      .then(() => {
        this.carsList.map((car) => {
          car.subscription
            = this.allSubscriptions.find((subs) => subs.carUUID === car.uuid) || car.subscription;
          return car;
        });
        this.currentCarList = this.getCurrentCarList();
        this.createGroup();
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  ngOnInit() {
    this.store
      .pipe(takeUntil(this.unsubscribe$), select(selectUserData))
      .subscribe((userData: any) => {
        this.userData = userData;
      });

    this.appMetrica.reportEvent('Страница "Подписка на услугу"', {
      'Название услуги': this.product.name,
    });
    this.reInitForm();
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
